(() => {
  if (window.__tdHomepageRedesignLoaded) return;
  window.__tdHomepageRedesignLoaded = true;

  const controllers = new WeakMap();
  const processControllers = new WeakMap();

  const initializeDragTrack = (track) => {
    if (!track || controllers.has(track)) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    let dragging = false;
    let moved = false;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    const lifestyle = track.closest('[data-td-home-lifestyle]');
    const previousButton = lifestyle?.querySelector('[data-td-lifestyle-previous]');
    const nextButton = lifestyle?.querySelector('[data-td-lifestyle-next]');
    let updateFrame = 0;

    const setButtonState = (button, hidden) => {
      if (!button) return;
      button.disabled = hidden;
      button.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    };

    const updateButtons = () => {
      updateFrame = 0;
      const hasOverflow = track.scrollWidth > track.clientWidth + 2;
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      setButtonState(previousButton, !hasOverflow || atStart);
      setButtonState(nextButton, !hasOverflow || atEnd);
    };

    const requestButtonUpdate = () => {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(updateButtons);
    };

    const finish = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = null;
      requestButtonUpdate();
    };

    track.addEventListener('pointerdown', (event) => {
      // Touch and pen use native scrolling, including momentum and vertical page gestures.
      if (event.pointerType !== 'mouse' || event.button !== 0) {
        moved = false;
        return;
      }
      dragging = true;
      moved = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScroll = track.scrollLeft;
      track.setPointerCapture(pointerId);
      track.classList.add('is-dragging');
    }, options);

    track.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (!moved && Math.abs(deltaX) < 6) return;
      if (!moved && Math.abs(deltaY) > Math.abs(deltaX)) return;
      moved = true;
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = startScroll - deltaX;
    }, { ...options, passive: false });

    track.addEventListener('pointerup', finish, options);
    track.addEventListener('pointercancel', finish, options);
    track.addEventListener('lostpointercapture', () => {
      dragging = false;
      pointerId = null;
      track.classList.remove('is-dragging');
      requestButtonUpdate();
    }, options);
    track.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, { ...options, capture: true });

    if (previousButton || nextButton) {
      const resizeObserver = new ResizeObserver(requestButtonUpdate);
      resizeObserver.observe(track);

      const scrollOneItem = (direction) => {
        const item = track.querySelector('.td-home-lifestyle__item');
        if (!item) return;
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
        track.scrollBy({
          left: direction * (item.getBoundingClientRect().width + gap),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };

      previousButton?.addEventListener('click', () => scrollOneItem(-1), options);
      nextButton?.addEventListener('click', () => scrollOneItem(1), options);
      track.addEventListener('scroll', requestButtonUpdate, { ...options, passive: true });
      if ('onscrollend' in track) track.addEventListener('scrollend', requestButtonUpdate, options);
      controller.signal.addEventListener('abort', () => {
        resizeObserver.disconnect();
        if (updateFrame) window.cancelAnimationFrame(updateFrame);
      }, { once: true });
      requestButtonUpdate();
    }

    controllers.set(track, controller);
  };

  const initializeProcess = (process) => {
    if (!process || processControllers.has(process)) return;

    const grid = process.querySelector('[data-td-process-grid]');
    const cursor = process.querySelector('[data-td-process-cursor]');
    if (!grid || !cursor) return;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = null;
    let pointerX = -200;
    let pointerY = -200;

    const renderPosition = () => {
      frame = null;
      const halfWidth = Math.max(56, cursor.offsetWidth / 2);
      const halfHeight = Math.max(22, cursor.offsetHeight / 2);
      const x = Math.min(Math.max(pointerX, halfWidth), window.innerWidth - halfWidth);
      const y = Math.min(Math.max(pointerY, halfHeight), window.innerHeight - halfHeight);
      cursor.style.setProperty('--td-process-cursor-x', `${x}px`);
      cursor.style.setProperty('--td-process-cursor-y', `${y}px`);
    };

    const positionCursor = (event) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      const overCard = Boolean(event.target.closest('.td-home-process__step'));
      cursor.classList.toggle('is-over-card', overCard);
      process.classList.toggle('is-cursor-active', overCard);

      if (reducedMotion.matches) {
        renderPosition();
        return;
      }

      if (frame === null) frame = window.requestAnimationFrame(renderPosition);
    };

    const hideCursor = () => {
      process.classList.remove('is-cursor-active');
      cursor.classList.remove('is-over-card');
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
    };

    grid.addEventListener('pointerenter', (event) => {
      if (!finePointer.matches) return;
      positionCursor(event);
    }, options);
    grid.addEventListener('pointermove', (event) => {
      if (!finePointer.matches) return;
      positionCursor(event);
    }, options);
    grid.addEventListener('pointerleave', hideCursor, options);
    grid.addEventListener('pointercancel', hideCursor, options);
    window.addEventListener('blur', hideCursor, options);
    finePointer.addEventListener('change', hideCursor, options);
    controller.signal.addEventListener('abort', hideCursor, { once: true });

    processControllers.set(process, controller);
  };

  const initialize = (root = document) => {
    if (root.matches?.('[data-td-drag-track]')) initializeDragTrack(root);
    root.querySelectorAll?.('[data-td-drag-track]').forEach(initializeDragTrack);
    if (root.matches?.('[data-td-process]')) initializeProcess(root);
    root.querySelectorAll?.('[data-td-process]').forEach(initializeProcess);
  };

  const destroy = (root) => {
    const targets = [];
    if (controllers.has(root)) targets.push(root);
    root.querySelectorAll?.('[data-td-drag-track]').forEach((target) => targets.push(target));
    targets.forEach((target) => {
      controllers.get(target)?.abort();
      controllers.delete(target);
    });

    const processes = [];
    if (processControllers.has(root)) processes.push(root);
    root.querySelectorAll?.('[data-td-process]').forEach((process) => processes.push(process));
    processes.forEach((process) => {
      processControllers.get(process)?.abort();
      processControllers.delete(process);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
